import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ExportToolDataService } from '../../export-tool/export-tool-data.service';
import { WxArticleVO } from '@article/model/vo/wx-article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ParsedArticleModelData } from '@3d-scene/model3d/cache/model-data';
import { WxCarrierElementMesh } from '../../export-tool/wx-carrier-element-mesh';
import { ExportToolUtils } from '../../export-tool/export-tool-utils';
import { Replace3DModelHelper } from '../../admintool-scene/helper/replace-3D-model.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleVO } from '@article/model/vo/article.vo';
import { AdmintoolProjectService } from '../../project/admintool-project.service';
import { FileUtils } from '@shared/util/file-utils';
import { ModelLoadingService } from '@article/service/model-loading.service';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

export class LoadArticleNotFoundError extends Error {
  constructor(message: string, public articleIdOrNumber: string) {
    super(message);
  }
}

export class LoadArticleParseError extends Error {
  constructor(message: string) {
    super(message);
  }
}

export class LoadArticleFileLoadError extends Error {
  constructor(message: string) {
    super(message);
  }
}

export interface LoadedArticle {
  wxArticle: WxArticleVO;
  article: ArticleVO;
}

@Injectable({
  providedIn: 'root'
})
export class LoadArticleService {

  constructor(
    private exportToolDataService: ExportToolDataService,
    private exportToolUtils: ExportToolUtils,
    private admintoolProjectService: AdmintoolProjectService,
    private replace3DModelHelper: Replace3DModelHelper,
    private undoRedoManager: UndoRedoHelper,
    private modelLoadingService: ModelLoadingService,
  ) {
  }

  /**
   * Imports an article from Wx database and provides it to the application.
   * @param articleIdOrNumber The article ID or number to search for in the Wx database.
   * @throws LoadArticleNotFoundError Thrown when no articles found.
   * @throws LoadArticleParseError Thrown when the article cannot be parsed.
   */
  public async loadArticleFromWxDatabase(articleIdOrNumber: string): Promise<LoadedArticle> {
    const wxArticles: WxArticleVO[] = await this.findWxArticles(articleIdOrNumber);
    const wxArticle: WxArticleVO = wxArticles[0];
    const parsedArticleModelData = await this.parseArticle(wxArticle, true);
    const article = await this.admintoolProjectService.createEmptyArticle(wxArticle.itemId, wxArticle.itemNo);
    await this.replace3DModelHelper.modelParseCallbackFn(parsedArticleModelData, article, true);
    article.isImportedFile = false;
    this.undoRedoManager.clearAll();
    return {wxArticle, article};
  }

  /**
   * Imports an article from Wx database and replaces the model of the given article.
   * @param article The article to replace the model for.
   * @param articleIdOrNumber The article ID or number to search for in the Wx database.
   * @throws LoadArticleNotFoundError Thrown when no articles found.
   * @throws LoadArticleParseError Thrown when the article cannot be parsed.
   */
  public async replaceArticleFromWxDatabase(article: ArticleVO, articleIdOrNumber: string): Promise<void> {
    const wxArticles = await this.findWxArticles(articleIdOrNumber);
    const parsedArticleModelData = await this.parseArticle(wxArticles[0], false);
    await this.replace3DModelHelper.modelParseCallbackFn(parsedArticleModelData, article, true);
    article.isImportedFile = false;
  }

  private async findWxArticles(articleIdOrNumber: string): Promise<WxArticleVO[]> {
    const wxArticles = await lastValueFrom(this.exportToolDataService.loadWxArticles(articleIdOrNumber));
    if (ArrayUtils.isEmpty(wxArticles)) {
      throw new LoadArticleNotFoundError('admintool.importArticle.alert.notFound.message', articleIdOrNumber);
    }
    return wxArticles;
  }

  private async parseArticle(wxArticleVO: WxArticleVO, parseConnections: boolean): Promise<ParsedArticleModelData> {
    const carrierElement: WxCarrierElementMesh = new WxCarrierElementMesh(wxArticleVO, this.exportToolDataService);
    try {
      await carrierElement.invalidateMesh(this.exportToolUtils, parseConnections);
      return new ParsedArticleModelData(carrierElement);
    } catch (error) {
      if (error instanceof Error) {
        throw new LoadArticleParseError(error.message);
      }
      throw new LoadArticleParseError('Unknown error occurred while parsing the article.');
    }
  }

  /**
   * Imports an article from a file and provides it to the application.
   * @throws LoadArticleFileLoadError Thrown when the file cannot be loaded.
   * @throws LoadArticleParseError Thrown when the article cannot be parsed.
   */
  public async loadArticleFromFile(): Promise<ArticleVO> {
    const file = await this.loadFile();
    const article: ArticleVO = await this.admintoolProjectService.createEmptyArticle();
    await this.replaceArticleFromModelFileInternal(article, file, false);
    article.isImportedFile = true;
    return article;
  }

  /**
   * Imports an article from a file and replaces the model of the given article.
   * @param article The article to replace the model for.
   * @throws LoadArticleFileLoadError Thrown when the file cannot be loaded.
   * @throws LoadArticleParseError Thrown when the article cannot be parsed.
   */
  public async replaceArticleFromFile(article: ArticleVO): Promise<void> {
    const file = await this.loadFile();
    await this.replaceArticleFromModelFileInternal(article, file, true);
    article.isImportedFile = true;
  }

  private async replaceArticleFromModelFileInternal(article: ArticleVO, file: File, keepConnections: boolean): Promise<void> {
    const parsedArticleModelData = await this.loadModel(file);
    this.prefixModelDataGeometryNames(parsedArticleModelData);
    const isGltfFile = file.name.substring(file.name.lastIndexOf('.'), file.name.length) === '.glb';
    if (isGltfFile) {
      await this.replace3DModelHelper.modelParseCallbackFnGltf(parsedArticleModelData, article, keepConnections);
    } else {
      await this.replace3DModelHelper.modelParseCallbackFnZip(parsedArticleModelData, article, keepConnections);
    }
  }

  private async loadFile(): Promise<File> {
    const file = await FileUtils.loadFile('.obz', '.zip', '.glb');
    if (!file) {
      throw new LoadArticleFileLoadError('File could not be loaded.');
    }
    return file;
  }

  private async loadModel(file: File): Promise<ParsedArticleModelData> {
    try {
      if (file.name.substring(file.name.lastIndexOf('.'), file.name.length) === '.glb') {
        return this.modelLoadingService.parseModelFromGlbFileAsync(file);
      } else {
        return this.modelLoadingService.parseModelFromZipFile(file);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new LoadArticleParseError(error.message);
      }
      throw new LoadArticleParseError('Unknown error occurred while parsing the article.');
    }
  }

  private prefixModelDataGeometryNames(parsedArticleModelData: ParsedArticleModelData | undefined) {
    parsedArticleModelData?.modelData?.traverse((child) => {
      if (IsThreeJsType.mesh(child)) {
        child.name = 's' + child.name;
      }
    });
  }
}
